<template>
  <div
    :style="{ minHeight: this.computedSize }"
    class="tw:w-full tw:flexcol-4"
    :class="{ 'tw:invisible': !isImageParsed }"
  >
    <div class="tw:flexcenter">
      <img ref="cropper_img" :src="imageSrc" alt="Profile picture" class="tw:w-full" />
    </div>
    <div class="tw:w-full tw:flexrow-2 tw:items-center tw:pb-4">
      <v-icon small>mdi-minus</v-icon>
      <v-slider
        v-if="isImageParsed"
        v-model="currentStep"
        @input="onZoomChanged"
        :step="zoomStep"
        :max="zoomSteps"
        :min="0"
        hide-details
      ></v-slider>
      <v-icon small>mdi-plus</v-icon>
    </div>
  </div>
</template>

<script>
import Cropper from 'cropperjs';

let cropper;

export default {
  name: 'CropperWrapper',
  props: {
    size: {
      type: Number,
      default: 200,
    },
    imageSrc: {
      type: String,
      default: null,
    },
    zoomSteps: {
      type: Number,
      default: 20,
    },
  },
  data: () => ({
    zoomStep: 0.025,
    minZoom: 0,
    currentStep: 0,
    isImageParsed: false,
  }),
  computed: {
    computedSize() {
      return `${this.size}px`;
    },
  },
  methods: {
    async generateImage(options) {
      if (!cropper) return;
      const result = await cropper.getCropperCanvas().$toCanvas(options);
      return result;
    },
    onZoomChanged(e) {
      const image = cropper.getCropperImage();
      let computedTransform = image.$getTransform();
      const newZoom = this.minZoom + this.zoomStep * e;
      computedTransform[0] = computedTransform[3] = newZoom;
      image.$setTransform(computedTransform);
    },
    exceededAxis(selection, maxSelection) {
      let stack = [];
      if (selection.x < parseFloat(maxSelection.x.toFixed(5))) stack.push('-x');
      if (selection.y < parseFloat(maxSelection.y.toFixed(5))) stack.push('-y');
      if (selection.x + selection.width > maxSelection.x + maxSelection.width) stack.push('+x');
      if (selection.y + selection.height > maxSelection.y + maxSelection.height) stack.push('+y');
      return stack;
    },
    transformEvent(event) {
      const cropperCanvas = cropper.getCropperCanvas();
      const cropperImage = cropper.getCropperImage();
      const cropperSelection = cropper.getCropperSelection();

      /** Creating clone to compute new position to check if it is valid */
      const cropperImageClone = cropperImage.cloneNode();
      cropperImageClone.style.transform = `matrix(${event.detail.matrix.join(', ')})`;
      cropperImageClone.style.opacity = '0';
      cropperCanvas.appendChild(cropperImageClone);
      const cropperImageRect = cropperImageClone.getBoundingClientRect();
      cropperCanvas.removeChild(cropperImageClone);
      /** */

      const cropperCanvasRect = cropperCanvas.getBoundingClientRect();

      const selection = cropperSelection;
      const maxSelection = {
        x: cropperImageRect.left - cropperCanvasRect.left,
        y: cropperImageRect.top - cropperCanvasRect.top,
        width: cropperImageRect.width,
        height: cropperImageRect.height,
      };
      const exceededAxisArray = this.exceededAxis(selection, maxSelection);
      if (exceededAxisArray.length > 0) {
        if (exceededAxisArray.length < 4) {
          /** Correct image position on zooming out */
          const matrixX = event.detail.matrix[4];
          const matrixY = event.detail.matrix[5];
          const oldMatrixZoom = event.detail.oldMatrix[0];
          const matrixZoom = event.detail.matrix[0];
          const newZoom = matrixZoom - oldMatrixZoom;
          if (newZoom !== 0 && matrixZoom < oldMatrixZoom) {
            const maxSelectionWidth = maxSelection.x + maxSelection.width;
            const selectionWidth = selection.x + selection.width;
            const maxSelectionHeight = maxSelection.y + maxSelection.height;
            const selectionHeight = selection.y + selection.height;
            const newMatrix = [
              matrixZoom,
              0,
              0,
              matrixZoom,
              exceededAxisArray.includes('-x')
                ? matrixX - (maxSelection.x - selection.x)
                : exceededAxisArray.includes('+x')
                  ? matrixX - (maxSelectionWidth - selectionWidth)
                  : matrixX,
              exceededAxisArray.includes('-y')
                ? matrixY - (maxSelection.y - selection.y)
                : exceededAxisArray.includes('+y')
                  ? matrixY - (maxSelectionHeight - selectionHeight)
                  : matrixY,
            ];
            cropperImage.$setTransform(...newMatrix);
          } else if (newZoom === 0) {
            /** Allow smooth panning on edges */
            if (exceededAxisArray.includes('-x') || exceededAxisArray.includes('+x'))
              cropperImage.$move(0, matrixY - event.detail.oldMatrix[5]);
            else if (exceededAxisArray.includes('-y') || exceededAxisArray.includes('+y'))
              cropperImage.$move(matrixX - event.detail.oldMatrix[4], 0);
          }
        }
        event.preventDefault();
      }
    },
    async initImage() {
      const selection = cropper.getCropperSelection();
      const image = cropper.getCropperImage();
      image.removeEventListener('transform', this.transformEvent);
      const centeredImage = image.$center();
      const imageSize = centeredImage.getBoundingClientRect();
      const shortestSide = imageSize.height < imageSize.width ? imageSize.height : imageSize.width;
      const squareSize = selection.height + 1;
      const newScale = shortestSide > squareSize ? (squareSize / shortestSide) * -1 : squareSize / shortestSide;
      const scaledImage = centeredImage.$scale(newScale);
      await this.$nextTick();
      this.minZoom = scaledImage.$getTransform()[0];
      this.isImageParsed = true;
      setTimeout(() => scaledImage.addEventListener('transform', this.transformEvent), 10);
    },
    async initCropper() {
      await this.$nextTick();
      cropper = new Cropper(this.$refs.cropper_img, {
        template: `
          <cropper-canvas scale-step="0" background>
            <cropper-image scalable translatable></cropper-image>
            <cropper-shade hidden theme-color="rgba(255,255,255,1)" class="tw:rounded-full"></cropper-shade>
            <cropper-handle plain></cropper-handle>
            <cropper-selection initial-coverage="1" aspect-ratio="1" class="tw:rounded-full">
              <cropper-handle action="move" plain class="tw:rounded-full"></cropper-handle>
            </cropper-selection>
          </cropper-canvas>
        `,
      });
      await this.$nextTick();
      setTimeout(() => this.initImage(), 10);
    },
    destroyCropper() {
      this.isImageParsed = false;
      if (!cropper) return;
      const image = cropper.getCropperImage();
      image.removeEventListener('transform', this.transformEvent);
      cropper = undefined;
    },
  },
  watch: {
    imageSrc: {
      handler(nV) {
        if (!nV) return this.destroyCropper();
        this.initCropper();
      },
      immediate: true,
    },
  },
  beforeDestroy() {
    this.destroyCropper();
  },
};
</script>

<style scoped>
:deep(cropper-canvas) {
  height: v-bind(computedSize);
  width: v-bind(computedSize);
  min-height: v-bind(computedSize);
  min-width: v-bind(computedSize);
}
</style>
